/**
 * Hooks React Query pour les emails (liste, envoi)
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query'
import type {
  ListEmailsQueryDto,
  PaginatedEmailsResponse,
} from '@sphere/communication/domain'
import { fetchEmails, sendEmail } from '../services'

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const emailKeys = {
  all: ['communication', 'emails'] as const,
  lists: () => [...emailKeys.all, 'list'] as const,
  list: (params: ListEmailsQueryDto) => [...emailKeys.lists(), params] as const,
}

/** Requête par défaut pour la sidebar « Derniers envois » */
export const defaultRecentEmailsQuery: ListEmailsQueryDto = {
  page: 1,
  limit: 5,
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useEmails(
  params: ListEmailsQueryDto = {},
  options?: Omit<
    UseQueryOptions<PaginatedEmailsResponse>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<PaginatedEmailsResponse>({
    queryKey: emailKeys.list(params),
    queryFn: () => fetchEmails(params),
    ...options,
  })
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useSendEmail() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: sendEmail,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: emailKeys.all })
    },
  })
}
